import { inject } from 'vue';
import { DateTime } from 'luxon';
// @ts-ignore — spark-md5 ships without type declarations
import SparkMD5 from 'spark-md5';
import CONST from '@/globals/constants.js';
import { useFileUploadStorage } from '@/composables/file-upload/use-file-upload-storage';
import { useFileUploadApi, type MediaInstanceResult } from '@/composables/file-upload/use-file-upload-api';
import { TARGET_COMPONENTS } from '@/composables/file-upload/use-file-uploader';
import { useCognitoAuthStore } from '@base-shared/stores/cognito-auth';
import type Utils from '@/globals/utils.js';
import type ApiError from '@/globals/api-error';
import type { components } from '@base-shared/types/media.openapi';
import type { FileUploadItem } from '@base-shared/types/file-uploader';

// NOTE: Incomplete type for media resource item openapi spec
type MediaItem = components['schemas']['MediaItem'] & Record<string, unknown>;
type MediaType = components['schemas']['MediaType'];

/**
 * Full details of a schedule item, including events and sidebar state.
 */
type ScheduleItemFullDetails = Record<string, unknown> & {
  scheduleId: string;
  events?: Array<Record<string, unknown>>;
  addsEvents?: Array<Record<string, unknown>>;
  sideBar?: { selectedDay?: { fullDate: string } };
} | null;

/**
 * Runtime dependencies passed to {@link useFileUploadQueue().advanceFile} on each call,
 * supplying Vuex / router state that the composable cannot own.
 */
export type AdvanceFileStateAction = 'addFileIntoLocalStorage' | 'getFullMediaDetailsSuccess' | 'addToScheduleRequestStarted' | 'addToScheduleRequestSuccess' | 'getFullMediaDetailsError' | 'updateMediaDataRequestStarted' | 'updateMediaDataRequestSuccess' | 'uploading' | 'failed';
export interface AdvanceFileDeps {
  /** Full schedule-item details from Vuex (needed when the upload targets the schedule page). */
  scheduleDetails: ScheduleItemFullDetails;
  /** Current media orientation string from `$dataStore.getMediaOrientation()`. */
  orientation: string | null;
  /**
   * Called after every localStorage write so the caller can broadcast Vuex events.
   * Equivalent to the former `emitEventToVuex(action, { files, updatedFile })`.
   */
  onStateChanged: (
    action: AdvanceFileStateAction,
    files: FileUploadItem[],
    updatedFile: FileUploadItem
  ) => void;
  /**
   * Called when a file is ready to be removed from the queue.
   * The composable removes it from localStorage first; the caller emits the Vuex event.
   */
  onDelete: (file: FileUploadItem) => void;
}

// ─── Constants (mirrors file-uploader.js) ───────────────────
const MEDIA_PAGES = new Set([
  TARGET_COMPONENTS.FILES_PAGE,
  TARGET_COMPONENTS.ALERT_PAGE,
  TARGET_COMPONENTS.LED_PAGE,
  TARGET_COMPONENTS.VIDEOWALL_PAGE,
  TARGET_COMPONENTS.TEMPLATES_PAGE,
  TARGET_COMPONENTS.EDIT_TEMPLATE_PAGE,
  TARGET_COMPONENTS.AUDIO_PAGE
]);
const DEFAULT_CHUNK_SIZE = 2097152; // 2 MB

/**
 * Composable that owns the core file-upload orchestration logic:
 *
 * 1. **MD5 hashing** – `hashFile(rawFile)` computes the pre-upload content hash.
 * 2. **Create-instance response processing** – `applyCreateInstanceResult(files, result)`
 *    is a pure function that maps an API result onto the in-flight files array.
 * 3. **Post-S3 pipeline** – `advanceFile(file, deps)` drives each file through the
 *    conversion-finished → fetch details → save schedule / apply metadata → delete cycle,
 *    using {@link useFileUploadStorage} for persistence and {@link useFileUploadApi}
 *    for HTTP calls.
 *
 * TODO(file-upload-v2): Things that are not handled here:
 *  - Element Plus dropzone refs and `submit()` / `clearFiles()` calls
 *  - Vuex state reads and dispatches (supplied via {@link AdvanceFileDeps} callbacks)
 *  - EventBus notifications
 */
export function useFileUploadQueue () {
  const storage = useFileUploadStorage();
  const api = useFileUploadApi();
  const cognitoAuthStore = useCognitoAuthStore();
  const $utils = inject('$utils') as typeof Utils;
  const $apiError = inject('$apiError') as typeof ApiError;

  // Dedup guards: track which files have already had a request started,
  // preventing duplicate API calls if `advanceFile` is called multiple times
  // for the same file while awaiting a response.
  // TODO(file-upload-v2): Check if this should be cleaned up since it currently
  // only pushes to the set and never removes.
  const filesWithGetFullMediaDetailsRequest = new Set<string>();
  const filesWithSaveNewScheduleRequest = new Set<string>();

  // ── 1. MD5 hashing ────────────────────────────────────

  /**
   * Computes the SparkMD5 hash of a `File`, reading in `chunkSize`-byte chunks
   * (default 2 MB). Capped at 50 chunks (~100 MB) for performance.
   * Replaces the nested `FileReader` / `SparkMD5` block in `handleDropzoneFileChange`.
   */
  function hashFile (rawFile: File, chunkSize = DEFAULT_CHUNK_SIZE): Promise<string> {
    return new Promise((resolve, reject) => {
      const blobSlice =
        File.prototype.slice ||
        (File.prototype as File & { mozSlice: typeof File.prototype.slice }).mozSlice ||
        (File.prototype as File & { webkitSlice: typeof File.prototype.slice }).webkitSlice;

      const chunks = Math.ceil(rawFile.size / chunkSize);
      let currentChunk = 0;
      const spark = new SparkMD5.ArrayBuffer();
      const fileReader = new FileReader();

      const loadNext = () => {
        const start = currentChunk * chunkSize;
        const end = Math.min(start + chunkSize, rawFile.size);
        fileReader.readAsArrayBuffer(blobSlice.call(rawFile, start, end));
      };

      fileReader.onload = (e: ProgressEvent<FileReader>) => {
        spark.append(e.target!.result as ArrayBuffer);
        currentChunk++;
        if (currentChunk < chunks && currentChunk < 50) {
          loadNext();
        } else {
          resolve(spark.end());
        }
      };

      fileReader.onerror = () => reject(new Error('MD5 hashing failed'));

      loadNext();
    });
  }

  // ── 2. Create-instance response processing ────────────

  /**
   * Pure function that applies a {@link MediaInstanceResult} onto the in-flight
   * files array, returning a new array (input is not mutated).
   *
   * Handles three outcomes:
   * - **Success / existing file**: marks the file as already-converted.
   * - **Success / new file**: stores the S3 pre-signed POST data.
   * - **Error / duplicate name**: flags `DuplicateResourceNameError` for the pop-up.
   * - **Error / normal**: flags `normalError` for the pop-up.
   *
   * Replaces the body of `actionAfterCreatingMediaInstanceRequest` in the component.
   */
  function applyCreateInstanceResult (
    files: FileUploadItem[],
    result: MediaInstanceResult
  ): FileUploadItem[] {
    const { status, responseData, requestData } = result;
    return files.map(mediaItem => {
      if (mediaItem.uid !== requestData.uid) return mediaItem;

      const item = $utils.clone(mediaItem) as FileUploadItem;
      if (!item._edit) return mediaItem;
      item._edit.uploadRequests.createMediaInstanceStatus = status;
      item._edit.uploadRequests.createMediaInstanceResponseData = responseData;
      item._edit.uploadRequests.createMediaInstanceEnd = DateTime.now().toFormat('yyyy-MM-dd HH:mm:ss');

      if (status === 'success') {
        if (responseData.exists && !responseData.post) {
          // File already exists on S3 — mark as converted immediately
          item._edit.resourceId = responseData.resourceId as string;
          item._edit.uploadRequests.createMediaInstanceResponseData = responseData;
          item._edit.existingFile = true;

          // Mark as file conversion done if no status
          if ((responseData.preview && !(responseData.preview as Record<string, unknown>).status) || !responseData.preview) {
            item._edit.uploadRequests.conversionFinished = true;
            item._edit.uploadRequests.submitToS3Message = 'submitted';
            item._edit.uploadRequests.submitToS3Status = 'success';
          }
        } else {
          // New upload — store the pre-signed POST data for the S3 step
          item._edit.post = responseData.post as { url: string; fields: Record<string, string> };
          item._edit.resourceId = responseData.resourceId as string;
          item._edit.generatedFilename = responseData.filename as string;
          item._edit.uploadRequests.createMediaInstanceResponseData = {
            ...responseData,
            _newUploadMedia: true
          };
        }
      }

      if (status === 'error') {
        item._edit.uploadRequests.createMediaInstanceStatusErrorType = 'normalError';
        const responseName = responseData.name as string;
        if (responseName === 'DuplicateResourceNameError' || responseName === 'DuplicateResourceNameNoAccessError') {
          item._edit.uploadRequests.createMediaInstanceStatusErrorType = responseName;
          item._edit.uploadRequests.createMediaInstanceOrigRequestData = requestData as unknown as Record<string, unknown>;
        }
      }

      return item;
    });
  }

  // ── 3. Post-S3 pipeline (main state machine) ──────────

  // ──── Helpers ─────────────────────────────────────────
  const updateFileInLocalStorage = (file: FileUploadItem, action: AdvanceFileStateAction, deps: AdvanceFileDeps) => {
    storage.upsertFile(file);
    deps.onStateChanged(action, storage.getFiles(), file);
  };

  const scheduleFileDeletion = (file: FileUploadItem, deps: AdvanceFileDeps) => {
    window.setTimeout(() => {
      storage.removeFile(file);
      deps.onDelete(file);
    }, 10);
  };

  // ──── Schedule page upload  ───────────────────────────
  /**
   * Handles the post-S3 pipeline for files uploaded while the schedule page is open.
   * On conversion: fetches full media details, attaches a playtime entry, saves the
   * schedule via the API, then removes the file from the queue.
   * Before conversion: writes an optimistic row so the UI can show a pending entry.
   */
  function processSchedulePageUpload (
    file: FileUploadItem & { _edit: NonNullable<FileUploadItem['_edit']> },
    deps: AdvanceFileDeps,
    uploadedFilesSaved: FileUploadItem[],
    fileResourceId: string,
    fileConversionFinished: boolean
  ): void {
    const scheduleItemFullDetails = deps.scheduleDetails as ScheduleItemFullDetails;
    const targetScheduleId = file._edit.targetPlayerId;
    const isSmartAdds = file._edit.targetDropzoneId === 'fileUploaderSmartAdds';
    const newAddedMediaItem = (file._edit.getMediaFullDetails.media ??
      file._edit.uploadRequests?.createMediaInstanceResponseData);

    // Create new playtime object
    const createSchedulePlaytime = () => {
      let newPlayTimeIndx = uploadedFilesSaved.length + 1;
      const events = scheduleItemFullDetails?.events;
      const addsEvents = scheduleItemFullDetails?.addsEvents;
      if (events && !isSmartAdds) newPlayTimeIndx = events.length + 1;
      else if (addsEvents && isSmartAdds) newPlayTimeIndx = addsEvents.length + 1;

      const newPlaytimeVal: Record<string, unknown> = {
        daily: true,
        order: $utils.calculateSortOrderValue({ mediaIndex: newPlayTimeIndx }),
        uid: `${$utils.generateUniqueID()}-${newPlayTimeIndx}`,
        adds: isSmartAdds ? { repeat: 1 } : false,
        startTime: '00:00',
        repeatDays: [],
        duration: 86400,
        startDate: DateTime.now().toFormat('yyyy-MM-dd')
      };

      // Adjust insertion order for audio tracks
      const type = newAddedMediaItem?.type as MediaType;
      const audioTypes = [CONST.MEDIA_TYPE_VALUE_AUDIO_PLAYLIST, CONST.MEDIA_TYPE_VALUE_AUDIO] as MediaType[];
      if (audioTypes.includes(type) && !isSmartAdds && events?.length) {
        const audioIndices: number[] = [];
        events.forEach((event, i) => { if (event.audio) audioIndices.push(i); });
        const lastAudioIdx = audioIndices.length ? audioIndices[audioIndices.length - 1] : null;
        if (lastAudioIdx !== null) {
          const lastOrder = events[lastAudioIdx].order;
          const nextOrder = events[lastAudioIdx + 1]?.order ?? null;
          if (lastOrder && nextOrder) {
            newAddedMediaItem!.order = $utils.calculateSortOrderValue({ topSiblingOrder: lastOrder, bottomSiblingOrder: nextOrder });
          } else if (lastOrder) {
            newAddedMediaItem!.order = $utils.calculateSortOrderValue({ topSiblingOrder: lastOrder, bottomSiblingOrder: false });
          }
        } else {
          newAddedMediaItem!.order = $utils.calculateSortOrderValue({ forceToTop: true, previousOrderVal: events[0]?.order });
        }
      }

      // Adjust start date if the sidebar shows a day other than today
      const selectedDay = scheduleItemFullDetails?.sideBar?.selectedDay;
      if (selectedDay) {
        const schedStart = DateTime.fromISO(selectedDay.fullDate as string).startOf('day');
        if (!DateTime.now().hasSame(schedStart, 'day')) {
          newPlaytimeVal.startDate = schedStart.toFormat('yyyy-MM-dd');
          delete newPlaytimeVal.daily;
        }
      }

      return newPlaytimeVal;
    };

    const createMediaWithPlaytime = (mediaSrc: MediaItem) => {
      const playtime = createSchedulePlaytime();
      const media = { ...mediaSrc };
      // Defensive copy so mutation of the schedules array doesn't affect mediaSrc
      media.schedules = Array.isArray(mediaSrc.schedules) ? [...(mediaSrc.schedules as unknown[])] : [];
      const schedules = media.schedules as Array<Record<string, unknown>>;
      const idx = schedules.findIndex(s => s.target === targetScheduleId);
      if (idx >= 0) {
        (schedules[idx].custom as unknown[]).push(playtime);
      } else {
        schedules.push({ target: targetScheduleId, type: 'custom', custom: [playtime] });
      }
      return media;
    };

    const createRowForOptimisticUpdate = (uploadFinished: boolean) => {
      const playtime = createSchedulePlaytime();
      return [{
        audio: (newAddedMediaItem?.type as string) === 'audio',
        groupId: newAddedMediaItem?.groupId,
        resourceId: newAddedMediaItem?.resourceId,
        times: [playtime],
        scheduled: true,
        scheduleId: scheduleItemFullDetails?.scheduleId,
        ...playtime,
        media: newAddedMediaItem,
        skin: newAddedMediaItem?.skin ?? false,
        rowType: isSmartAdds ? 'smartAdds' : 'regularSchedule',
        optimisticUpdate: { fileUploadId: file.uid, uploadFinished, schedulePlaytimeAdded: false }
      }];
    };

    if (fileConversionFinished) {
      if (filesWithGetFullMediaDetailsRequest.has(file.uid)) return;
      filesWithGetFullMediaDetailsRequest.add(file.uid);

      api.fetchMediaDetails(fileResourceId, deps.orientation as 'horizontal' | 'vertical' | null)
        .then(responseData => {
          file._edit.getMediaFullDetails.requestStatus = 'success';
          file._edit.getMediaFullDetails.media = responseData;
          file._edit.addToSchedule.media = createMediaWithPlaytime(responseData);
          file._edit.addToSchedule.selectedPlayerScheduleDetails = scheduleItemFullDetails;
          file._edit.addToSchedule.optimisticUpdate = createRowForOptimisticUpdate(fileConversionFinished);
          updateFileInLocalStorage(file, 'getFullMediaDetailsSuccess', deps);

          if (filesWithSaveNewScheduleRequest.has(file.uid)) return;
          filesWithSaveNewScheduleRequest.add(file.uid);
          file._edit.addToSchedule.addToScheduleRequestStarted = DateTime.now().toFormat('yyyy-MM-dd HH:mm:ss');
          updateFileInLocalStorage(file, 'addToScheduleRequestStarted', deps);

          api.saveSchedule(fileResourceId, targetScheduleId!, file._edit.addToSchedule.media as Record<string, unknown>, deps.orientation)
            .then(response => {
              file._edit.addToSchedule.addToScheduleRequestEnd = DateTime.now().toFormat('yyyy-MM-dd HH:mm:ss');
              file._edit.addToSchedule.addToScheduleRequestStatus = 'success';
              const firstCustom = (response.schedules as Array<{ custom: Array<Record<string, unknown>> }>)?.[0]?.custom?.[0];
              const currentOptimistic = file._edit.addToSchedule.optimisticUpdate[0] as Record<string, unknown>;
              file._edit.addToSchedule.optimisticUpdate[0] = {
                ...currentOptimistic,
                order: firstCustom?.order,
                uid: firstCustom?.uid,
                optimisticUpdate: { ...(currentOptimistic.optimisticUpdate as Record<string, unknown>), schedulePlaytimeAdded: true }
              };
              updateFileInLocalStorage(file, 'addToScheduleRequestSuccess', deps);
              scheduleFileDeletion(file, deps);
            })
            .catch(error => {
              file._edit.addToSchedule.addToScheduleRequestEnd = DateTime.now().toFormat('yyyy-MM-dd HH:mm:ss');
              file._edit.addToSchedule.addToScheduleRequestStatus = 'error';
              updateFileInLocalStorage(file, 'addToScheduleRequestSuccess', deps);
              $apiError.handleAPIRequestError(error, {});
            });
        })
        .catch(error => {
          file._edit.getMediaFullDetails.requestStatus = 'error';
          file._edit.getMediaFullDetails.media = error;
          updateFileInLocalStorage(file, 'getFullMediaDetailsError', deps);
        });
    } else if (file.status && [
      CONST.FILE_UPLOAD_ITEM_STATUS.FAILED,
      CONST.FILE_UPLOAD_ITEM_STATUS.CANCEL
    ].includes(file.status)) {
      // Propagate the failed/cancelled status to the file's
      // local storage data before the deletion.
      updateFileInLocalStorage(file, 'failed', deps);
      scheduleFileDeletion(file, deps);
    } else {
      file._edit.getMediaFullDetails.requestStatus = null;
      file._edit.getMediaFullDetails.media = newAddedMediaItem ?? null;
      file._edit.addToSchedule.selectedPlayerScheduleDetails = scheduleItemFullDetails;
      file._edit.addToSchedule.optimisticUpdate = createRowForOptimisticUpdate(false);
      updateFileInLocalStorage(file, 'uploading', deps);
    }
  }

  // ──── Media page upload  ──────────────────────────────
  /**
   * Handles the post-S3 pipeline for files uploaded from a media page (files, alert, LED, etc.).
   * On conversion with deferred metadata: PUTs the pending metadata update then removes the file.
   * On conversion without deferred metadata: removes the file immediately.
   * Before conversion: writes the uploading state so the UI can show progress.
   */
  function processMediaPageUpload (
    file: FileUploadItem & { _edit: NonNullable<FileUploadItem['_edit']> },
    deps: AdvanceFileDeps,
    fileResourceId: string,
    fileConversionFinished: boolean
  ): void {
    const mediaDetails = (file._edit.getMediaFullDetails.media ??
      file._edit.uploadRequests.createMediaInstanceResponseData) as MediaItem;
    const pendingMetadata = file._edit.updateMediaDataOnceConversionFinished?.updateMediaRequestData ?? null;

    if (fileConversionFinished && pendingMetadata) {
      if (file._edit.updateMediaDataOnceConversionFinished.updateMediaDataRequestStarted) return;
      file._edit.updateMediaDataOnceConversionFinished.updateMediaDataRequestStarted = DateTime.now().toFormat('yyyy-MM-dd HH:mm:ss');
      updateFileInLocalStorage(file, 'updateMediaDataRequestStarted', deps);

      api.updateMediaData(fileResourceId, pendingMetadata)
        .then(() => {
          const updated = $utils.clone(file) as FileUploadItem & { _edit: NonNullable<FileUploadItem['_edit']> };
          updated._edit.updateMediaDataOnceConversionFinished.updateMediaDataRequestStatus = 'success';
          updated._edit.updateMediaDataOnceConversionFinished.updateMediaDataRequestEnd = DateTime.now().toFormat('yyyy-MM-dd HH:mm:ss');
          updateFileInLocalStorage(updated, 'updateMediaDataRequestSuccess', deps);
          scheduleFileDeletion(file, deps);
        })
        .catch(() => {}); // 401 handled by withRetry; other errors silently ignored (preserved behaviour)
    } else if (fileConversionFinished && !pendingMetadata) {
      // Propagate the conversion finished status to the
      // file's local storage data before the deletion
      // to update the UI accordingly.
      updateFileInLocalStorage(file, 'uploading', deps);
      scheduleFileDeletion(file, deps);
    } else if (file.status && [
      CONST.FILE_UPLOAD_ITEM_STATUS.FAILED,
      CONST.FILE_UPLOAD_ITEM_STATUS.CANCEL
    ].includes(file.status)) {
      // Propagate the failed/cancelled status to the file's
      // local storage data before the deletion.
      updateFileInLocalStorage(file, 'failed', deps);
      scheduleFileDeletion(file, deps);
    } else {
      file._edit.getMediaFullDetails.requestStatus = null;
      file._edit.getMediaFullDetails.media = mediaDetails ?? null;
      updateFileInLocalStorage(file, 'uploading', deps);
    }
  }

  /**
   * Advances a single file through the post-conversion pipeline after its S3 upload
   * completes or the conversion-finished signal arrives via `handleSVGMessage`.
   * Dispatches to {@link processSchedulePageUpload} or {@link processMediaPageUpload}
   * based on the file's target component.
   */
  function advanceFile (
    fileItem: FileUploadItem & { _edit: NonNullable<FileUploadItem['_edit']> },
    deps: AdvanceFileDeps
  ): void {
    if (!cognitoAuthStore.isAuthenticated) return;
    if (!fileItem) return;

    const filesInLocalStorage = storage.getFiles();
    const existing = filesInLocalStorage.find(item => item.uid === fileItem.uid);
    if (!existing) {
      storage.upsertFile(fileItem);
    } else if (!existing._edit && fileItem._edit) {
      updateFileInLocalStorage(fileItem, 'addFileIntoLocalStorage', deps);
    }

    const file = $utils.clone(fileItem) as FileUploadItem & { _edit: NonNullable<FileUploadItem['_edit']> };
    const uploadedFilesSaved = storage.getFiles();
    const targetComponent = file._edit.targetComponent ?? '';
    const fileResourceId = file._edit.uploadRequests?.createMediaInstanceResponseData?.resourceId as string | undefined;
    const fileConversionFinished = file._edit.uploadRequests?.conversionFinished ?? false;

    if (targetComponent === TARGET_COMPONENTS.SCHEDULE_PLAYER_SELECTED && fileResourceId) {
      processSchedulePageUpload(file, deps, uploadedFilesSaved, fileResourceId, fileConversionFinished);
    } else if (MEDIA_PAGES.has(targetComponent as Parameters<typeof MEDIA_PAGES.has>[0]) && fileResourceId) {
      processMediaPageUpload(file, deps, fileResourceId, fileConversionFinished);
    }
  }

  return {
    hashFile,
    applyCreateInstanceResult,
    advanceFile
  };
}
